/**
 * rubberband-page.js
 *
 * Lists all coins/stocks currently in a "rubberband" state on Daily and 4H (or 1H) timeframes.
 *
 * Rubberband definition (50 SMA) with RSI confirmation:
 *   DAILY  long:  price below a rising 50 SMA  +  weekly RSI > 50
 *   DAILY  short: price above a falling 50 SMA +  weekly RSI < 50
 *   4H     long:  price below a rising 50 SMA  +  daily RSI > 50
 *   4H     short: price above a falling 50 SMA +  daily RSI > 50
 *
 * Results are grouped by timeframe and sorted by distance from SMA
 * (most stretched first, since those have the strongest setup).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { fetchTopCoins } from '../services/coingecko-api';
import { fetchBinanceKlinesBatch } from '../services/binance-api';
import { buildSymbolIndex, isStablecoin } from '../utils/symbol-mapper';
import { parseBinanceKlines, extractPrices } from '../utils/timeframe-utils';
import { getRubberbandState } from '../indicators/sma-50';
import { calculateRSI, calculateStochastic } from '../utils/technical-indicators';
import { formatPrice, formatPercentChange } from '../utils/format-helpers';
import { fetchServerRubberbandData, fetchStocksRubberbandData, isServerApiAvailable } from '../services/server-api';
import './rubberband-page.css';

// ─── Timeframe config ─────────────────────────────────────────────────────────
// Weekly is used only for RSI confirmation (not displayed as its own section)
const TIMEFRAMES = [
  { key: 'daily',    label: 'Daily',   interval: '1d', icon: '📆' },
  { key: 'fourHour', label: '4 Hour',  interval: '4h', icon: '⏰' },
];

const STOCK_TIMEFRAMES = [
  { key: 'daily',    label: 'Daily',   interval: '1d', icon: '📆' },
  { key: 'fourHour', label: '1 Hour',  interval: '1h', icon: '⏰' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDistanceFromSMA(pct) {
  if (pct === null) return '—';
  const abs = Math.abs(pct);
  return `${abs.toFixed(2)}%`;
}

// ─── Sub-component: rubberband table for one timeframe ────────────────────────
function RubberbandSection({ label, icon, interval, coins, isLoading }) {
  const navigate = useNavigate();

  const longCoins  = coins.filter(c => c.state === 'long').sort((a, b) => a.pctFromSMA - b.pctFromSMA);
  const shortCoins = coins.filter(c => c.state === 'short').sort((a, b) => b.pctFromSMA - a.pctFromSMA);
  const total      = longCoins.length + shortCoins.length;

  return (
    <div className="rb-section">
      <div className="rb-section-header">
        <div className="rb-section-title">
          <span className="rb-section-icon">{icon}</span>
          <span>{label}</span>
          <span className="rb-interval-badge">{interval.toUpperCase()}</span>
        </div>
        {!isLoading && (
          <div className="rb-section-count">
            {total === 0
              ? 'No setups'
              : `${longCoins.length} long · ${shortCoins.length} short`}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="rb-loading-row"><Loader size="small" text="Scanning..." /></div>
      ) : total === 0 ? (
        <div className="rb-empty">No rubberband setups on {label} right now.</div>
      ) : (
        <div className="rb-coins-grid">
          {/* Long setups */}
          {longCoins.map(coin => (
            <div
              key={`${coin.id}-long`}
              className="rb-coin-card long"
              onClick={() => navigate(`/coin/${coin.id}`)}
              title="Click for details"
            >
              <div className="rb-card-top">
                <div className="rb-coin-identity">
                  {coin.image && (
                    <img src={coin.image} alt={coin.name} className="rb-coin-logo"
                      onError={e => e.target.style.display = 'none'} />
                  )}
                  <div>
                    <div className="rb-coin-symbol">{coin.symbol?.toUpperCase()}</div>
                    <div className="rb-coin-name">{coin.name}</div>
                  </div>
                </div>
                <div className="rb-direction-badge long">
                  <span>▲</span> LONG
                </div>
              </div>

              <div className="rb-card-stats">
                <div className="rb-stat">
                  <div className="rb-stat-label">Price</div>
                  <div className="rb-stat-value">{formatPrice(coin.current_price)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">50 SMA</div>
                  <div className="rb-stat-value sma-green">{formatPrice(coin.sma)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">Below SMA</div>
                  <div className="rb-stat-value stretch-value">{formatDistanceFromSMA(coin.pctFromSMA)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">24h</div>
                  <div className={`rb-stat-value ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercentChange(coin.price_change_percentage_24h)}
                  </div>
                </div>
              </div>

              <div className="rb-sma-bar">
                <div className="rb-sma-bar-label">SMA slope</div>
                <div className="rb-slope-indicator up">↑ Rising</div>
              </div>
            </div>
          ))}

          {/* Short setups */}
          {shortCoins.map(coin => (
            <div
              key={`${coin.id}-short`}
              className="rb-coin-card short"
              onClick={() => navigate(`/coin/${coin.id}`)}
              title="Click for details"
            >
              <div className="rb-card-top">
                <div className="rb-coin-identity">
                  {coin.image && (
                    <img src={coin.image} alt={coin.name} className="rb-coin-logo"
                      onError={e => e.target.style.display = 'none'} />
                  )}
                  <div>
                    <div className="rb-coin-symbol">{coin.symbol?.toUpperCase()}</div>
                    <div className="rb-coin-name">{coin.name}</div>
                  </div>
                </div>
                <div className="rb-direction-badge short">
                  <span>▼</span> SHORT
                </div>
              </div>

              <div className="rb-card-stats">
                <div className="rb-stat">
                  <div className="rb-stat-label">Price</div>
                  <div className="rb-stat-value">{formatPrice(coin.current_price)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">50 SMA</div>
                  <div className="rb-stat-value sma-red">{formatPrice(coin.sma)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">Above SMA</div>
                  <div className="rb-stat-value stretch-value">{formatDistanceFromSMA(coin.pctFromSMA)}</div>
                </div>
                <div className="rb-stat">
                  <div className="rb-stat-label">24h</div>
                  <div className={`rb-stat-value ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercentChange(coin.price_change_percentage_24h)}
                  </div>
                </div>
              </div>

              <div className="rb-sma-bar">
                <div className="rb-sma-bar-label">SMA slope</div>
                <div className="rb-slope-indicator down">↓ Falling</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
function RubberbandPage() {
  const [results, setResults]       = useState({ daily: [], fourHour: [] });
  const [coinMeta, setCoinMeta]     = useState([]);  // for name/image lookup
  const [isLoading, setIsLoading]   = useState(true);
  const [error, setError]           = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'long' | 'short'
  const [assetMode, setAssetMode]   = useState('crypto');

  const scan = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // ── Stocks mode: VPS-only ──────────────────────────────────────────────
      if (assetMode === 'stocks') {
        if (!isServerApiAvailable()) {
          setError('Stocks data requires the VPS server. Start the server and refresh.');
          setIsLoading(false);
          return;
        }
        console.log('[RBPullback] Stocks mode — fetching pre-computed stocks rubberband data...');
        const { rubberband, lastUpdated: serverUpdated } = await fetchStocksRubberbandData();
        setResults({
          daily:    rubberband.daily    || [],
          fourHour: rubberband.fourHour || [],
        });
        setLastUpdated(new Date(serverUpdated).getTime());
        setIsLoading(false);
        console.log(`[RBPullback] Stocks: daily=${(rubberband.daily || []).length}, 1h=${(rubberband.fourHour || []).length}`);
        return;
      }

      // ── VPS fast-path: pre-computed rubberband states from the server ─────
      if (isServerApiAvailable()) {
        try {
          console.log('[RBPullback] VPS mode — fetching pre-computed rubberband data...');
          const { rubberband, lastUpdated: serverUpdated } = await fetchServerRubberbandData();
          setResults({
            daily:    rubberband.daily    || [],
            fourHour: rubberband.fourHour || [],
          });
          setLastUpdated(new Date(serverUpdated).getTime());
          setIsLoading(false);
          console.log(`[RBPullback] VPS: daily=${(rubberband.daily || []).length}, 4h=${(rubberband.fourHour || []).length}`);
          return;
        } catch (vpsErr) {
          if (vpsErr.message === 'Data not yet available') {
            console.log('[RBPullback] Server initialising — will retry when ready');
            setError('Server is starting up and pre-computing indicators. This takes about 60 seconds — please refresh in a moment.');
            setIsLoading(false);
            return;
          }
          console.warn('[RBPullback] VPS fetch failed, falling back to live scan:', vpsErr.message);
        }
      }

      // ── 1. Get coin metadata ──────────────────────────────────────────────
      const rawCoins = await fetchTopCoins(80);
      const coins    = rawCoins.filter(c => !isStablecoin(c.id));
      setCoinMeta(coins);

      const symbolIndex = buildSymbolIndex(coins);
      // Fast lookup: binanceSymbol → coin metadata
      const metaByBinance = {};
      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        const meta = coins.find(c => c.id === coinGeckoId);
        if (meta) metaByBinance[binanceSymbol] = meta;
      });

      // ── 2. Fetch klines for all 3 timeframes in parallel ─────────────────
      // Weekly klines are used for RSI confirmation on daily setups (not displayed)
      const requests = (interval, limit) =>
        symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval, limit }));

      const [weeklyRaw, dailyRaw, fourHourRaw] = await Promise.all([
        fetchBinanceKlinesBatch(requests('1w', 60)),
        fetchBinanceKlinesBatch(requests('1d', 100)),
        fetchBinanceKlinesBatch(requests('4h', 200)),
      ]);

      // ── 3. Evaluate rubberband state for each coin / timeframe ────────────
      // Strategy:
      //   Daily long/short:  SMA condition + weekly RSI confirmation
      //   4H long/short:     SMA condition + daily RSI confirmation (both dirs need daily RSI > 50)
      const tfResults = { daily: [], fourHour: [] };

      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        const meta = metaByBinance[binanceSymbol];
        if (!meta) return;

        const currentPrice = meta.current_price;

        // Helper: parse raw klines → full OHLCV price arrays
        const getOHLCV = (raw) => {
          if (!raw || raw.length === 0) return null;
          const prices = extractPrices(parseBinanceKlines(raw));
          return prices.closes && prices.closes.length >= 52 ? prices : null;
        };

        const wData  = getOHLCV(weeklyRaw[binanceSymbol]);
        const dData  = getOHLCV(dailyRaw[binanceSymbol]);
        const h4Data = getOHLCV(fourHourRaw[binanceSymbol]);

        const wCloses  = wData?.closes  ?? null;
        const dCloses  = dData?.closes  ?? null;
        const h4Closes = h4Data?.closes ?? null;

        // Pre-compute RSI and Stoch(5,2,3) for confirmation
        const lastVal = arr => (arr && arr.length > 0) ? arr[arr.length - 1] : null;
        const weeklyRsi = wCloses ? lastVal(calculateRSI(wCloses)) : null;
        const dailyRsi  = dCloses ? lastVal(calculateRSI(dCloses)) : null;

        const { k: dStochArr }   = dData  ? calculateStochastic(dData.highs,  dData.lows,  dData.closes,  5, 2, 3) : { k: [] };
        const { k: h4StochArr }  = h4Data ? calculateStochastic(h4Data.highs, h4Data.lows, h4Data.closes, 5, 2, 3) : { k: [] };
        const dailyStochK = lastVal(dStochArr);
        const h4StochK    = lastVal(h4StochArr);

        const coinEntry = {
          id:                          coinGeckoId,
          name:                        meta.name,
          symbol:                      meta.symbol,
          image:                       meta.image,
          current_price:               currentPrice,
          price_change_percentage_24h: meta.price_change_percentage_24h,
          market_cap_rank:             meta.market_cap_rank,
        };

        // Daily rubberband: weekly RSI + Stoch(5,2,3) %K filter
        if (dCloses) {
          const { state, sma, slope, pctFromSMA } = getRubberbandState(currentPrice, dCloses);
          const passes = (state === 'long'  && weeklyRsi != null && weeklyRsi > 50 && dailyStochK != null && dailyStochK < 20)
                      || (state === 'short' && weeklyRsi != null && weeklyRsi < 50 && dailyStochK != null && dailyStochK > 80);
          if (passes) {
            tfResults.daily.push({ ...coinEntry, state, sma, slope, pctFromSMA });
          }
        }

        // 4H rubberband: daily RSI (both dirs > 50) + Stoch(5,2,3) %K filter
        if (h4Closes) {
          const { state, sma, slope, pctFromSMA } = getRubberbandState(currentPrice, h4Closes);
          const passes = (state === 'long'  && dailyRsi != null && dailyRsi > 50 && h4StochK != null && h4StochK < 20)
                      || (state === 'short' && dailyRsi != null && dailyRsi > 50 && h4StochK != null && h4StochK > 80);
          if (passes) {
            tfResults.fourHour.push({ ...coinEntry, state, sma, slope, pctFromSMA });
          }
        }
      });

      setResults(tfResults);
      setLastUpdated(Date.now());

    } catch (err) {
      console.error('[RBPullback] Scan failed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [assetMode]);

  useEffect(() => { scan(); }, [scan]);

  // ─── Asset mode toggle ────────────────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    if (newMode === assetMode) return;
    setAssetMode(newMode);
    // scan() will be re-triggered via useEffect dependency on assetMode via useCallback
  };

  // ── Derived totals for the summary bar ──────────────────────────────────────
  const allCoins   = [...results.daily, ...results.fourHour];
  const totalLong  = allCoins.filter(c => c.state === 'long').length;
  const totalShort = allCoins.filter(c => c.state === 'short').length;

  // ── Filter helper ────────────────────────────────────────────────────────────
  const filterCoins = (coins) => {
    if (activeFilter === 'long')  return coins.filter(c => c.state === 'long');
    if (activeFilter === 'short') return coins.filter(c => c.state === 'short');
    return coins;
  };

  const activeTimeframes = assetMode === 'stocks' ? STOCK_TIMEFRAMES : TIMEFRAMES;

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="rubberband-page">
        <div className="asset-toggle">
          <button
            className={`asset-toggle-btn ${assetMode === 'crypto' ? 'active' : ''}`}
            onClick={() => handleModeChange('crypto')}
          >
            🪙 Crypto
          </button>
          <button
            className={`asset-toggle-btn ${assetMode === 'stocks' ? 'active' : ''}`}
            onClick={() => handleModeChange('stocks')}
          >
            📈 US Stocks
          </button>
        </div>
        <div className="rb-page-header">
          <h1 className="rb-page-title">🏹 RBPullback Trades</h1>
          <p className="rb-page-subtitle">Scanning all {assetMode === 'stocks' ? 'stocks' : 'coins'} for 50 SMA RBPullback setups...</p>
        </div>
        <div className="rb-loading-center">
          <Loader size="large" text="Scanning all timeframes..." />
        </div>
      </div>
    );
  }

  return (
    <div className="rubberband-page">
      {/* Asset mode toggle */}
      <div className="asset-toggle">
        <button
          className={`asset-toggle-btn ${assetMode === 'crypto' ? 'active' : ''}`}
          onClick={() => handleModeChange('crypto')}
        >
          🪙 Crypto
        </button>
        <button
          className={`asset-toggle-btn ${assetMode === 'stocks' ? 'active' : ''}`}
          onClick={() => handleModeChange('stocks')}
        >
          📈 US Stocks
        </button>
      </div>

      {/* Page Header */}
      <div className="rb-page-header">
        <div className="rb-header-main">
          <h1 className="rb-page-title">🏹 RBPullback Trades</h1>
          <p className="rb-page-subtitle">
            Price stretched away from 50 SMA · Daily: price vs rising/falling SMA + weekly RSI · {assetMode === 'stocks' ? '1H' : '4H'}: + daily RSI &gt; 50
          </p>
        </div>
        <button className="rb-refresh-btn" onClick={scan} title="Refresh scan">
          ↻ Refresh
        </button>
      </div>

      {error && (
        <ErrorBanner
          message={error}
          type="error"
          onDismiss={() => setError(null)}
          onRetry={scan}
        />
      )}

      {/* Summary bar */}
      <div className="rb-summary-bar">
        <div className="rb-summary-stat">
          <span className="rb-summary-number">{totalLong + totalShort}</span>
          <span className="rb-summary-label">Total Setups</span>
        </div>
        <div className="rb-summary-stat long-stat">
          <span className="rb-summary-number">{totalLong}</span>
          <span className="rb-summary-label">▲ Long</span>
        </div>
        <div className="rb-summary-stat short-stat">
          <span className="rb-summary-number">{totalShort}</span>
          <span className="rb-summary-label">▼ Short</span>
        </div>
        {lastUpdated && (
          <div className="rb-summary-updated">
            Updated {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="rb-filter-tabs">
        {['all', 'long', 'short'].map(f => (
          <button
            key={f}
            className={`rb-filter-tab ${activeFilter === f ? 'active' : ''} ${f !== 'all' ? f : ''}`}
            onClick={() => setActiveFilter(f)}
          >
            {f === 'all'   ? '⬛ All'      : ''}
            {f === 'long'  ? '▲ Long only' : ''}
            {f === 'short' ? '▼ Short only': ''}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="rb-legend">
        <div className="rb-legend-item">
          <span className="rb-legend-dot long"></span>
          <span><strong>Long setup:</strong> Price below rising 50 SMA · Stoch(5,2,3) %K &lt; 20 · Daily: weekly RSI &gt; 50 · {assetMode === 'stocks' ? '1H' : '4H'}: daily RSI &gt; 50</span>
        </div>
        <div className="rb-legend-item">
          <span className="rb-legend-dot short"></span>
          <span><strong>Short setup:</strong> Price above falling 50 SMA · Stoch(5,2,3) %K &gt; 80 · Daily: weekly RSI &lt; 50 · {assetMode === 'stocks' ? '1H' : '4H'}: daily RSI &gt; 50</span>
        </div>
      </div>

      {/* Timeframe sections */}
      {activeTimeframes.map(({ key, label, icon, interval }) => (
        <RubberbandSection
          key={key}
          label={label}
          icon={icon}
          interval={interval}
          coins={filterCoins(results[key])}
          isLoading={false}
        />
      ))}
    </div>
  );
}

export default RubberbandPage;
